import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import apiClient from '../services/api';
import { getCustomerId } from '../utils/authUser';

function Usage() {
  const { user, activeCustomerId } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usageData, setUsageData] = useState({
    tier: 'starter',
    pricing_model: 'AWS Marketplace contract with consumption',
    contract_mrr_estimate: 0,
    hourly_rate: 0,
    current_month: {
      month: '',
      hours: 0,
      billing_period_hours: 0,
      contract_mrr_estimate: 0,
      prorated_contract_estimate: 0,
      usage_overage_estimate: 0,
      cost: 0
    },
    days_remaining: 0,
    history: [],
    breakdown: [],
    billing_cycle: 'Monthly',
    payment_method: 'AWS Marketplace',
    next_invoice_date: ''
  });

  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        const customerId = activeCustomerId || getCustomerId(user);
        if (!customerId) {
          setError('No customer selected');
          setLoading(false);
          return;
        }

        const response = await apiClient.get(`/customers/${customerId}/usage`);
        setUsageData(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching usage data:', err);
        setError('Failed to load usage data');
      } finally {
        setLoading(false);
      }
    };

    fetchUsageData();
  }, [user, activeCustomerId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        <div className="loading-spinner" style={{ borderTopColor: 'var(--primary-color)' }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="page-header">
          <h1>Usage & Billing</h1>
        </div>
        <div className="card">
          <p style={{ color: 'var(--danger-color)' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Usage & Billing</h1>
      </div>

      {/* Current Plan */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h2>Current Plan</h2>
          <button className="btn btn-secondary">Upgrade Plan</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px' }}>
          <div>
            <p style={{ color: 'var(--text-muted)', marginBottom: '4px', fontSize: '14px' }}>Plan</p>
            <p style={{ fontWeight: 600, fontSize: '18px', textTransform: 'capitalize' }}>
              {usageData.tier || 'Starter'}
            </p>
          </div>
          <div>
            <p style={{ color: 'var(--text-muted)', marginBottom: '4px', fontSize: '14px' }}>Billing Cycle</p>
            <p style={{ fontWeight: 600, fontSize: '18px' }}>{usageData.billing_cycle}</p>
          </div>
          <div>
            <p style={{ color: 'var(--text-muted)', marginBottom: '4px', fontSize: '14px' }}>Next Invoice</p>
            <p style={{ fontWeight: 600, fontSize: '18px' }}>{usageData.next_invoice_date}</p>
          </div>
          <div>
            <p style={{ color: 'var(--text-muted)', marginBottom: '4px', fontSize: '14px' }}>Payment Method</p>
            <p style={{ fontWeight: 600, fontSize: '18px' }}>{usageData.payment_method}</p>
          </div>
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '20px' }}>
          Pricing model: {usageData.pricing_model || 'AWS Marketplace contract with consumption'}
        </p>
      </div>

      {/* Current Month Usage */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Current Month Usage ({usageData.current_month.month})</h3>
          <div className="stat-value">{usageData.current_month.hours}h</div>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '8px' }}>
            Instance hours out of {usageData.current_month.billing_period_hours || '-'}h
          </p>
        </div>
        <div className="stat-card">
          <h3>MTD Total Est.</h3>
          <div className="stat-value">${usageData.current_month.cost.toFixed(2)}</div>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '8px' }}>
            Prorated contract plus metered overages
          </p>
        </div>
        <div className="stat-card">
          <h3>Contract MRR Est.</h3>
          <div className="stat-value">${Number(usageData.current_month.contract_mrr_estimate || usageData.contract_mrr_estimate || 0).toFixed(2)}</div>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '8px' }}>
            Full-month Marketplace contract equivalent
          </p>
        </div>
        <div className="stat-card">
          <h3>Overage Est.</h3>
          <div className="stat-value">${Number(usageData.current_month.usage_overage_estimate || 0).toFixed(2)}</div>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '8px' }}>
            Storage and data transfer overages
          </p>
        </div>
      </div>

      {/* Usage History */}
      <div className="card">
        <div className="card-header">
          <h2>Billing History</h2>
        </div>
        {usageData.history.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Usage</th>
                <th>MTD Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {usageData.history.map((item, index) => (
                <tr key={index}>
                  <td>{item.month}</td>
                  <td>{item.hours} hours</td>
                  <td>${item.cost.toFixed(2)}</td>
                  <td>
                    <span className={`badge badge-${item.status === 'paid' ? 'success' : 'warning'}`}>
                      {item.status === 'paid' ? 'Paid' : 'Pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>
            No billing history available yet.
          </p>
        )}
      </div>

      {/* Usage Breakdown */}
      <div className="card" style={{ marginTop: '24px' }}>
        <div className="card-header">
          <h2>Usage Breakdown</h2>
        </div>
        {usageData.breakdown.length > 0 ? (
          <table className="table usage-breakdown-table">
            <thead>
              <tr>
                <th>Resource</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {usageData.breakdown.map((item, index) => (
                <tr key={index}>
                  <td>{item.resource}</td>
                  <td>{item.quantity}</td>
                  <td>{item.unit_price}</td>
                  <td>{item.total}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3" style={{ textAlign: 'right', fontWeight: 600 }}>Total</td>
                <td style={{ fontWeight: 700, fontSize: '18px' }}>${usageData.current_month.cost.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>
            No usage breakdown available yet.
          </p>
        )}
      </div>

      {/* Payment Info */}
      <div className="card" style={{ marginTop: '24px' }}>
        <div className="card-header">
          <h2>Payment Information</h2>
        </div>
        <p style={{ color: 'var(--text-muted)' }}>
          Your subscription is billed through AWS Marketplace. The estimates above prorate the Marketplace contract MRR by
          metered instance hours and add any metered overages. AWS Marketplace remains the source of truth for invoices,
          taxes, refunds, and private-offer charges. To update your payment method or view detailed invoices, please visit
          your <a href="https://console.aws.amazon.com/marketplace/home" target="_blank" rel="noopener noreferrer">AWS Marketplace subscriptions</a>.
        </p>
      </div>
    </div>
  );
}

export default Usage;
